import { useNavigate } from "react-router-dom";
import { useT } from "../i18n";

export type TabKey = "home" | "clans" | "prizes" | "profile";

const ACTIVE = "#9E84FF";
const INACTIVE = "#6B6B73";

function HomeIcon({ color }: { color: string }) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 10.5 12 4l9 6.5" />
      <path d="M5 9.5V20h14V9.5" />
      <path d="M10 20v-5h4v5" />
    </svg>
  );
}

function ClansIcon({ color }: { color: string }) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 19v-1.2A3.8 3.8 0 0 1 7.3 14h3.4a3.8 3.8 0 0 1 3.8 3.8V19" />
      <path d="M16 5.2a3 3 0 0 1 0 5.6" />
      <path d="M17.5 14a3.6 3.6 0 0 1 3 3.5V19" />
    </svg>
  );
}

function PrizesIcon({ color }: { color: string }) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 4h8v5a4 4 0 0 1-8 0V4z" />
      <path d="M8 5H5.5A1.5 1.5 0 0 0 4 6.5C4 8.4 5.6 10 7.5 10H8" />
      <path d="M16 5h2.5A1.5 1.5 0 0 1 20 6.5C20 8.4 18.4 10 16.5 10H16" />
      <path d="M12 13v3" />
      <path d="M9 20h6l-.6-3H9.6L9 20z" />
    </svg>
  );
}

function ProfileIcon({ color }: { color: string }) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="3.2" />
      <path d="M5.5 19.5a6.5 6.5 0 0 1 13 0" />
    </svg>
  );
}

const ICONS: Record<TabKey, (p: { color: string }) => React.ReactNode> = {
  home: HomeIcon,
  clans: ClansIcon,
  prizes: PrizesIcon,
  profile: ProfileIcon,
};

export default function TabBar({ active, phone }: { active: TabKey; phone: string }) {
  const nav = useNavigate();
  const { t } = useT();
  const q = `?phone=${encodeURIComponent(phone)}`;

  const tabs: { key: TabKey; labelKey: string; to: string }[] = [
    { key: "home", labelKey: "tab.home", to: `/hub${q}` },
    { key: "clans", labelKey: "tab.clans", to: `/clan${q}` },
    { key: "prizes", labelKey: "tab.prizes", to: `/prizes${q}` },
    { key: "profile", labelKey: "tab.profile", to: `/profile${q}` },
  ];

  return (
    <nav className="fixed bottom-0 inset-x-0 z-40 bg-bg border-t border-line">
      <div className="max-w-md mx-auto flex items-start justify-around px-3 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        {tabs.map((tab) => {
          const isActive = tab.key === active;
          const color = isActive ? ACTIVE : INACTIVE;
          const Icon = ICONS[tab.key];
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => nav(tab.to)}
              className="flex flex-col items-center gap-1.5 flex-1"
            >
              <Icon color={color} />
              <span
                className="text-[11px]"
                style={{ color, fontWeight: isActive ? 700 : 600 }}
              >
                {t(tab.labelKey)}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
